import RestartAltIcon from '@mui/icons-material/RestartAlt';
import { Button, LinearProgress } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import dayjs from 'dayjs';
import { observer } from 'mobx-react-lite';
import { useEffect } from 'react';
import { API } from '~/api';
import { AppRoutes } from '~/app/app.routes';
import { AuthService } from '~/service/service.auth';
import { PostService } from '~/service/service.post';
import { UIService } from '~/service/service.ui';
import { useService } from '~/shared/service/service.base';
import { PostsScoreDialog } from '~/view/dialog/dialog.posts.score';
import {
	ActionsColumn,
	AvatarColumn,
	DateTimeColumn,
	GridColumn,
	SecondaryColumn,
	useGridColumns,
} from '~/view/grid/grid.columns';
import { GridContainer } from '~/view/grid/grid.container';
import { GridNoRows } from '~/view/grid/grid.norows';
import { MuiGridPagination } from '~/view/grid/grid.pagination';
import { GridToolbarView } from '~/view/grid/grid.toolbar';
import { Text } from '~/view/view.box';

export const PostListPage = observer(() => {
	const [posts, ui, auth] = useService(PostService, UIService, AuthService);
	const columns = useGridColumns<API.PostItem>(() => [
		AvatarColumn({ field: 'image' }),
		{ field: 'id', headerName: 'ID' },
		SecondaryColumn({
			field: 'fragrance',
			headerName: 'Fragrance',
			secondary: r => r.brand,
			minWidth: 200,
			align: 'left',
		}),
		DateTimeColumn({ field: 'createdAt', headerName: 'Created At' }),
		DateTimeColumn({ field: 'publishAt', headerName: 'Publish At' }),
		ScoreColumn(),
		{ field: 'user', headerName: 'User Name', minWidth: 200 },
		{ field: 'text', headerName: 'Text', flex: 1 },
		ActionsColumn({
			editRoute: AppRoutes.PostEdit.buildPath,
			permission: 'post',
		}),
	]);

	ui.usePageTitle('Posts');
	useEffect(() => posts.loadAndSubscribe(), [posts]);

	return (
		<GridContainer async={posts.async}>
			<DataGrid
				initialState={posts.gridState.initial()}
				loading={posts.async.loading}
				rowCount={posts.totalCount}
				rows={posts.items.slice()}
				columns={columns}
				disableColumnFilter
				paginationMode="server"
				sortingMode="server"
				onStateChange={posts.gridState.onStateChange}
				slots={{
					loadingOverlay: LinearProgress,
					noRowsOverlay: () => <GridNoRows>No posts found</GridNoRows>,
					pagination: MuiGridPagination,
					toolbar: () => (
						<GridToolbarView
							onReload={posts.reload}
							gridState={posts.gridState}
							onCreateUrl={auth.can('post', 'CREATE') ? AppRoutes.PostNew.buildPath({}) : undefined}
							exportEnabled={auth.can('post', 'EXPORT')}
							rightMenu={auth.can('post', 'UPDATE') ? <RecalculateButton /> : undefined}
						/>
					),
				}}
			/>
			<PostsScoreDialog />
		</GridContainer>
	);
});

const RecalculateButton = observer(() => {
	const [ui] = useService(UIService);

	return (
		<Button size="small" startIcon={<RestartAltIcon />} onClick={() => ui.showDialog(PostsScoreDialog)}>
			Recalculate
		</Button>
	);
});

function ScoreColumn(): GridColumn<API.PostItem> {
	return {
		field: 'score',
		headerName: 'Score',
		width: 90,
		filterable: false,
		renderCell: ({ row }) => {
			const boost = row.publishAt ? row.score - dayjs(row.publishAt).valueOf() / 3_600_000 : 0;
			return (
				<Text variant="body2" color={boost < 0 ? 'error.main' : 'success.main'}>
					{boost >= 0 ? '+' : ''}
					{boost.toFixed(1)}h
				</Text>
			);
		},
	};
}
